import type { ReactNode } from "react";

import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { Amount } from "./Amount.js";

export interface BoxHeadingProps {
  name: string;
  /** A short line above the name: a game, a series, "Limited". */
  eyebrow?: string;
  description?: string;
  /** The price of one open, in integer cents. */
  price: number;
  /** Beside the price: "per box · 1 card". */
  priceNote?: string;
  /** Under the price: a VolatilityMeter, a StockBadge. */
  meta?: ReactNode;
  /** The name's heading level: h1 on the box's own page, h2 where the page has its own h1. */
  as?: "h1" | "h2";
  align?: "start" | "center";
  className?: string;
}

/** A box's name and price at the top of its page: eyebrow, name, description, price and note. */
export function BoxHeading({
  name,
  eyebrow,
  description,
  price,
  priceNote,
  meta,
  as = "h1",
  align = "start",
  className,
}: BoxHeadingProps) {
  const center = align === "center";
  return (
    <div className={cn("flex flex-col gap-5", center && "items-center text-center", className)}>
      <div className={cn("flex flex-col gap-2", center && "items-center")}>
        {eyebrow && (
          <Text variant="caption-eyebrow" tone="highlight">
            {eyebrow}
          </Text>
        )}
        <Text as={as} variant="display-xl2" tone="primary">
          {name}
        </Text>
        {description && (
          <Text variant="body-b1" tone="secondary" className="max-w-md">
            {description}
          </Text>
        )}
      </div>
      <div className="flex items-baseline gap-3">
        <Amount cents={price} size="lg" className="text-text-primary" />
        {priceNote && (
          <Text variant="body-b3" tone="tertiary">
            {priceNote}
          </Text>
        )}
      </div>
      {meta}
    </div>
  );
}
